import { useEffect } from 'react';
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { testNotificationsControllerGetTestDeviceStatus } from '@/generated/korza-api-client';
import { useTenant } from '@/hooks/use-tenant';
import { getApiErrorMessage } from '@/lib/api-error';
import { LINK_POLL_INTERVAL_MS } from '@/lib/test-device-link';

/**
 * Whether the logged-in admin has a test device linked in this store
 * (`GET test-notifications/device`, PUSH-08). Polls every 3 s while `poll`
 * and refetches when the tab becomes visible again while `enabled` (the
 * store app is opened in front of the admin, R10).
 */
export function useTestDeviceStatus(opts: {
  enabled: boolean;
  poll: boolean;
}): UseQueryResult<{ linked: boolean }> {
  const { enabled, poll } = opts;
  const tenant = useTenant();

  const query = useQuery({
    queryKey: ['test-device-status', tenant.slug],
    queryFn: async () => {
      const result = await testNotificationsControllerGetTestDeviceStatus({
        path: { tenantSlug: tenant.slug },
      });
      const { status } = result.response;
      if (status < 200 || status >= 300 || !result.data) {
        throw new Error(getApiErrorMessage(result.error, `HTTP ${status}`));
      }
      return { linked: result.data.linked };
    },
    enabled,
    refetchInterval: poll ? LINK_POLL_INTERVAL_MS : false,
    // The visibility listener below covers it (also with a fresh cache).
    refetchOnWindowFocus: false,
  });

  const { refetch } = query;
  useEffect(() => {
    if (!enabled) return;
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') void refetch();
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () =>
      document.removeEventListener('visibilitychange', onVisibilityChange);
  }, [enabled, refetch]);

  return query;
}
